import React, { useContext, useState } from "react";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { useGetTicketHistory } from "../api/tickets";
import {
  Text,
  Group,
  Flex,
  Avatar,
  Badge,
  Box,
  ActionIcon,
  MultiSelect,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import {
  IconChevronDown,
  IconChevronRight,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import dayjs from "dayjs";

import { DataTable } from "../components/DataTable";
import TicketHistoryDashboard from "../components/TicketHistory/TicketHistoryDashboard";
import { ChangesList } from "../components/Registrations/ChangesList";
import {
  HISTORY_TYPES,
  displaySummary,
  getFieldChanges,
  getHistoryType,
  toChangesList,
} from "../components/TicketHistory/utils";

const PER_PAGE = 20;

function TicketHistory() {
  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);

  const [page, setPage] = useState(1);
  const [types, setTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [expandedRows, setExpandedRows] = useState([]);

  const { data, isFetching } = useGetTicketHistory({
    eventId,
    page,
    limit: PER_PAGE,
    types: types.length ? types.join(",") : null,
    search: debouncedSearch.trim() || null,
  });

  const toggleRow = (id) =>
    setExpandedRows((rows) =>
      rows.includes(id) ? rows.filter((row) => row !== id) : [...rows, id]
    );

  return (
    <Flex direction="column" pt="sm">
      <TicketHistoryDashboard eventId={eventId} />

      <DataTable
        mt="md"
        records={data?.data ?? []}
        fetching={isFetching}
        page={page}
        onPageChange={setPage}
        recordsPerPage={PER_PAGE}
        totalRecords={data?.pagination?.total ?? 0}
        verticalSpacing="md"
        rowExpansion={{
          allowMultiple: true,
          expanded: { recordIds: expandedRows },
          content: ({ record }) => (
            <Box p="md" bg="gray.0">
              <Text fw={600} size="sm" mb="xs">
                Change Details:
              </Text>
              {displaySummary(record) && (
                <Text size="sm" c="dimmed" mb="xs">
                  {displaySummary(record)}
                </Text>
              )}
              {getFieldChanges(record).length > 0 && (
                <ChangesList changes={toChangesList(getFieldChanges(record))} />
              )}
            </Box>
          ),
        }}
        columns={[
          {
            accessor: "createdAt",
            title: "Time",
            width: 190,
            render: (record) => (
              <Group gap="xs" wrap="nowrap">
                <ActionIcon
                  variant="light"
                  color="gray"
                  size="sm"
                  onClick={() => toggleRow(record.id)}
                >
                  {expandedRows.includes(record.id) ? (
                    <IconChevronDown size={14} />
                  ) : (
                    <IconChevronRight size={14} />
                  )}
                </ActionIcon>
                <Text size="sm">
                  {dayjs(record.createdAt).format("DD MMM YYYY, HH:mm")}
                </Text>
              </Group>
            ),
          },
          {
            accessor: "changeStatus",
            title: "Action",
            width: 170,
            render: ({ changeStatus }) => {
              const { label, color, icon: Icon } = getHistoryType(changeStatus);

              return (
                <Badge
                  leftSection={<Icon size={16} />}
                  color={color}
                  variant="light"
                >
                  {label}
                </Badge>
              );
            },
            filter: (
              <MultiSelect
                label="Action"
                description="Filter by action type"
                placeholder="All actions"
                data={HISTORY_TYPES}
                value={types}
                onChange={(value) => {
                  setTypes(value);
                  setPage(1);
                }}
                clearable
                comboboxProps={{ withinPortal: false }}
              />
            ),
            filtering: types.length > 0,
          },
          {
            accessor: "holder",
            title: "Ticket holder",
            render: ({ firstName, lastName, email, picture }) => (
              <Group gap="sm" wrap="nowrap">
                <Avatar
                  src={picture}
                  size="md"
                  radius="sm"
                  alt="avatar"
                  color="initials"
                  name={`${firstName} ${lastName}`}
                />
                <Flex direction="column">
                  <Text size="sm">
                    {firstName} {lastName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {email}
                  </Text>
                </Flex>
              </Group>
            ),
            filter: (
              <TextInput
                label="Ticket holder"
                description="Filter by name or email"
                placeholder="Search holder..."
                leftSection={<IconSearch size={16} />}
                rightSection={
                  search && (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => {
                        setSearch("");
                        setPage(1);
                      }}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  )
                }
                value={search}
                onChange={(event) => {
                  setSearch(event.currentTarget.value);
                  setPage(1);
                }}
              />
            ),
            filtering: Boolean(search),
          },
          {
            accessor: "changes",
            title: "Changes",
            width: 140,
            render: (record) => {
              const count = getFieldChanges(record).length;

              return (
                <Text size="sm" c="dimmed">
                  {count
                    ? `${count} ${count === 1 ? "field" : "fields"} changed`
                    : "—"}
                </Text>
              );
            },
          },
          {
            accessor: "changedBy",
            title: "By",
            render: ({ changedBy }) => (
              <Flex direction="column">
                <Text size="sm">
                  {changedBy?.firstName} {changedBy?.lastName}
                </Text>
                <Text size="xs" c="dimmed">
                  {changedBy?.role}
                </Text>
              </Flex>
            ),
          },
        ]}
      />
    </Flex>
  );
}

export default TicketHistory;
